import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Radio, RadioGroup, Spinner } from "@heroui/react";
import { setSelectedShipping } from "../../reducers/slices/selection";
import { setContentModal, setMainModal } from "../../reducers/slices/main";
import { get_basket_thunk } from "../../reducers/slices/basket/thunks/get_basket_thunk";
import { shipping_rates } from "../../api/calls/shipping/shipping_rates";
import { apply_starshipit_rate } from "../../api/calls/basket/apply_starshipit_rate";
import { getAddressFromLatLng } from "../../api/calls/google/get_address_from_lat_lng";
import useTotalBasketWeight from "./hooks/useTotalBasketWeight";
import { buildAddressObject } from "../address/helpers/address";
import { NoContentMessage } from "../no_content_message";
import { ListShimmerX3 } from "../shimmer/shimmer_types";
export const ShippingCost = () => {
    const dispatch = useDispatch();
    const { address, branch, selection } = useSelector((state) => state.selectionState);
    const { address: branchAddress } = branch || {};
    const { selectedShipping } = selection || {};
    const { basket } = useSelector((state) => state.basketState);
    const { product_list } = basket || { product_list: [] };
    const [shippingOptions, setShippingOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isUpdating, setIsUpdating] = useState(false);
    const totalWeight = useTotalBasketWeight(product_list);
    useEffect(() => {
        fetchShippingOptions(totalWeight);
    }, [totalWeight]);
    const fetchShippingOptions = async (weight) => {
        const { latitude, longitude } = address || {};
        try {
            const [userAddressResponse, branchAddressResponse] = await Promise.all([
                getAddressFromLatLng({
                    latitude: latitude || 0,
                    longitude: longitude || 0,
                }),
                getAddressFromLatLng({
                    latitude: branchAddress?.latitude || 0,
                    longitude: branchAddress?.longitude || 0,
                }),
            ]);
            if (userAddressResponse.success && branchAddressResponse.success) {
                const userLocation = { ...userAddressResponse.content, description: userAddressResponse.content.formatted_address };
                const branchLocation = { ...branchAddressResponse.content, description: branchAddressResponse.content.formatted_address };
                const destination = buildAddressObject(userLocation);
                const sender = buildAddressObject(branchLocation);
                try {
                    const response = await shipping_rates({
                        sender: {
                            street: `${sender?.street_number || ""} ${sender?.street_name || ""}`,
                            suburb: sender?.suburb || "",
                            city: sender?.city || "",
                            state: sender?.province || "",
                            post_code: sender?.postal_code || "",
                            country_code: sender?.country_code || "",
                        },
                        destination: {
                            street: `${destination?.street_number || ""} ${destination?.street_name || ""}`,
                            suburb: destination?.suburb || "",
                            city: destination?.city || "",
                            state: destination?.province || "",
                            post_code: destination?.postal_code || "",
                            country_code: destination?.country_code || "",
                        },
                        packages: [
                            {
                                weight: weight,
                            },
                        ],
                    });
                    setIsLoading(false);
                    if (response.success) {
                        console.log("Shipping rates response:", response);
                        // Map Starshipit rates to our interface format
                        const rates = response.content?.rates || response.content?.data?.rates || [];
                        const mappedRates = rates.map((rate) => ({
                            service_code: rate.service_code || rate.serviceCode || rate.code,
                            service_name: rate.service_name || rate.serviceName || rate.service || rate.name,
                            total_price: rate.total_price || rate.price || rate.totalPrice || 0,
                            // Preserve all original fields for when we apply the rate
                            ...rate,
                        }));
                        setShippingOptions(mappedRates);
                    }
                    else {
                        console.error("Failed to fetch shipping rates:", response.message);
                        setShippingOptions([]);
                    }
                }
                catch (error) {
                    setIsLoading(false);
                    console.error("Error fetching shipping rates:", error);
                }
            }
            else {
                setIsLoading(false);
                dispatch(setMainModal({ title: "Error", messages: ["Error fetching address."] }));
            }
        }
        catch (error) {
            setIsLoading(false);
            console.error("Error fetching addresses:", error);
            dispatch(setMainModal({ title: "Error", messages: ["Error fetching address."] }));
        }
    };
    const onOptionSelected = async (optionCode) => {
        setIsUpdating(true);
        const selectedOption = shippingOptions.find((option) => option.service_code === optionCode) || null;
        dispatch(setSelectedShipping(selectedOption));
        if (!selectedOption || !basket?._id) {
            setIsUpdating(false);
            dispatch(setMainModal({ title: "Error", messages: ["Missing shipping option or basket ID"] }));
            return;
        }
        try {
            // Apply Starshipit rate to basket using the new endpoint
            // Map all available fields from the selected option to match backend expectations
            const response = await apply_starshipit_rate({
                basket_id: basket._id,
                rate: {
                    // Required field
                    price: selectedOption.total_price || selectedOption.price || 0,
                    // Optional fields - map from various possible field names
                    rate_id: selectedOption.rate_id || selectedOption.id,
                    id: selectedOption.id || selectedOption.rate_id,
                    carrier: selectedOption.carrier || selectedOption.carrier_name,
                    carrier_name: selectedOption.carrier_name || selectedOption.carrier,
                    service: selectedOption.service || selectedOption.service_name,
                    service_name: selectedOption.service_name || selectedOption.service,
                    service_code: selectedOption.service_code || selectedOption.code,
                    currency: selectedOption.currency,
                    estimated_delivery_date: selectedOption.estimated_delivery_date || selectedOption.delivery_date,
                    delivery_date: selectedOption.delivery_date || selectedOption.estimated_delivery_date,
                },
            });
            const selectedPrice = parseFloat(String(selectedOption.total_price ?? selectedOption.price ?? "0")) || 0;
            if (!response.success) {
                setIsUpdating(false);
                if (selectedPrice > 0) {
                    dispatch(setMainModal({ title: "Something went wrong!", messages: [response.message] }));
                }
                else {
                    dispatch(setContentModal(null));
                }
                return;
            }
            // Refresh basket to get updated totals with the new delivery fee
            await dispatch(get_basket_thunk());
            dispatch(setContentModal(null));
            setIsUpdating(false);
        }
        catch (error) {
            setIsUpdating(false);
            console.error("Error applying Starshipit rate:", error);
            dispatch(setMainModal({ title: "Error", messages: ["Failed to apply shipping rate. Please try again."] }));
        }
    };
    if (product_list.length === 0)
        return null;
    return (React.createElement("div", null, isLoading ? (React.createElement("div", null,
        React.createElement(ListShimmerX3, null))) : shippingOptions.length === 0 ? (React.createElement(NoContentMessage, { messages: ["No shipping options available."] })) : (React.createElement("div", { className: "flex flex-col gap-2" },
        React.createElement(RadioGroup, { classNames: { wrapper: "gap-3" }, isRequired: true, value: selectedShipping?.service_code, name: "shipping-fee-list", onValueChange: (value) => onOptionSelected(value) }, shippingOptions?.map((option) => (React.createElement("div", { key: option?.service_code, className: "flex flex-row justify-between items-center w-full gap-2 border border-neutral-gray rounded-xl px-4 py-3" },
            React.createElement("div", { className: "flex flex-row gap-4 justify-evenly" },
                React.createElement(Radio, { value: option?.service_code ?? "" }),
                React.createElement("div", { className: "flex flex-col" },
                    React.createElement("div", { className: "text-primary" }, option?.service_name),
                    React.createElement("div", { className: "text-primary font-semibold" },
                        "AUD",
                        option?.total_price))),
            React.createElement("div", { className: "self-start" },
                React.createElement("div", { className: "rounded-full bg-gray text-xs px-2 py-1" }, option?.service_code)))))),
        React.createElement("div", { className: "flex justify-center h-[1.3em]" }, isUpdating && (React.createElement("div", { className: "flex flex-row gap-3 items-center" },
            React.createElement(Spinner, { size: "sm", color: "primary" }),
            React.createElement("div", { className: "text-primary text-sm" }, "Updating basket..."))))))));
};
